import {
  type CompiledFolderNode,
  type CompiledFolderNodeCreateAction,
  type CompiledFrameworkStructure,
  evaluateCollectionMembership,
  type ParentValues,
} from "@repo/shared";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/app-errors";
import { captureError, isErrorObserved } from "@/lib/observability";
import type { TreeItem } from "../types";

export type CreateActionParentEntity = {
  alias: string;
  entityTypeKey: string;
  entityId: string;
  entitySlug: string;
};

export type ResolvedCreateAction = {
  sourceNodeKey: string;
  sourcePath?: string;
  action: CompiledFolderNodeCreateAction;
  parentEntities?: CreateActionParentEntity[];
};

export function buildCreateActionFailureMessage(action: CompiledFolderNodeCreateAction) {
  const label = action.label.trim() || action.targetEntityTypeKey;
  return `Could not complete ${label} creation. Refresh before trying again.`;
}

// One interaction, one entity: the create controls stay enabled while a create is in flight,
// and `onDuplicate: "suffix"` means a second call would commit a second, suffixed entity
// instead of converging on the first. The key is the concrete placement — project, compiled
// node, preset, target, source path, and parent entity ids — so only repeated clicks on the
// same control are suppressed: "Add Field" under another dataset, the same preset in another
// kept-alive project tab, or any other distinct placement proceeds. JSON keeps the parts
// unambiguous without a hand-rolled delimiter.
const inFlightCreateActions = new Set<string>();

function buildCreateActionInFlightKey(projectId: string, action: ResolvedCreateAction) {
  return JSON.stringify([
    projectId,
    action.sourceNodeKey,
    action.action.presetKey,
    action.action.targetEntityTypeKey,
    action.sourcePath ?? null,
    (action.parentEntities ?? []).map((parent) => parent.entityId),
  ]);
}

/**
 * Settles a create-action promise at the UI event boundary. `CreateEntityButton` invokes
 * handlers with `void onSelect(...)`, so a rejected create must be caught here — otherwise it
 * leaks as an unhandled rejection and the button appears to do nothing. Re-entrant calls for
 * the same placement are dropped while one is in flight, so a double-click cannot create two
 * suffixed entities.
 */
export async function runGuardedCreateAction(
  projectId: string,
  action: ResolvedCreateAction,
  create: () => Promise<string>,
  onSuccess: (nextPath: string) => void
) {
  const inFlightKey = buildCreateActionInFlightKey(projectId, action);
  if (inFlightCreateActions.has(inFlightKey)) {
    return;
  }
  inFlightCreateActions.add(inFlightKey);
  try {
    const nextPath = await create();
    onSuccess(nextPath);
  } catch (error) {
    // Observed mutations have already reported their failures; anything else in the create flow
    // (folder lookup, preconditions) would vanish once this boundary settles the promise, so
    // report it before toasting.
    if (!isErrorObserved(error)) {
      captureError(error, {
        feature: "project-entities",
        operation: "create-action",
        extra: {
          targetEntityTypeKey: action.action.targetEntityTypeKey,
          presetKey: action.action.presetKey,
        },
      });
    }
    toast.error(getErrorMessage(error, buildCreateActionFailureMessage(action.action)));
  } finally {
    inFlightCreateActions.delete(inFlightKey);
  }
}

export function buildCreateParentScopeValues(
  parentEntities: CreateActionParentEntity[] | undefined
) {
  const parentScopeValues: Record<string, unknown> = {};

  for (const parent of parentEntities ?? []) {
    parentScopeValues[parent.alias] = {
      entityTypeKey: parent.entityTypeKey,
      entityId: parent.entityId,
      entitySlug: parent.entitySlug,
    };
    parentScopeValues[`${parent.alias}Id`] = parent.entityId;
    parentScopeValues[`${parent.alias}Slug`] = parent.entitySlug;
  }

  return parentScopeValues;
}

function buildCollectionParentValues(
  parentEntities: CreateActionParentEntity[] | undefined
): ParentValues {
  const parentValues: ParentValues = {};

  for (const parent of parentEntities ?? []) {
    parentValues[parent.alias] = {
      entityTypeKey: parent.entityTypeKey,
      entityId: parent.entityId,
      entitySlug: parent.entitySlug,
    };
  }

  return parentValues;
}

function getSortedCollectionNodes(
  structure: Pick<CompiledFrameworkStructure, "folderNodes"> | null
) {
  if (!structure?.folderNodes) return [];
  return [...structure.folderNodes]
    .filter((node): node is CompiledFolderNode => node.kind === "collection")
    .sort((left, right) => left.listOrder - right.listOrder);
}

function buildResolvedCreateActions(input: {
  node: CompiledFolderNode;
  parentEntities?: CreateActionParentEntity[];
}) {
  const createActions = input.node.createActions;
  if (!createActions?.length) {
    return [];
  }

  return createActions.map((action) => ({
    sourceNodeKey: input.node.key,
    action,
    parentEntities: input.parentEntities,
  }));
}

function withSourcePath(actions: ResolvedCreateAction[], sourcePath?: string) {
  if (!sourcePath) {
    return actions;
  }

  return actions.map((action) => ({
    ...action,
    sourcePath: action.sourcePath ?? sourcePath,
  }));
}

function joinPathSegments(...segments: Array<string | null | undefined>) {
  const path = segments
    .filter((segment): segment is string => !!segment)
    .map((segment) => segment.replace(/^\/+|\/+$/g, ""))
    .filter(Boolean)
    .join("/");

  return path ? `/${path}` : "/";
}

export function resolveNodeCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  nodeKey: string;
  parentEntities?: CreateActionParentEntity[];
}): ResolvedCreateAction[] {
  const node = (input.structure?.folderNodes ?? []).find(
    (candidate): candidate is CompiledFolderNode =>
      candidate.rootFolderSlug === input.rootFolderSlug && candidate.key === input.nodeKey
  );
  if (!node) {
    return [];
  }

  return buildResolvedCreateActions({
    node,
    parentEntities: input.parentEntities,
  });
}

export function resolveCollectionCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  collectionSlug: string;
  parentEntities?: CreateActionParentEntity[];
}): ResolvedCreateAction[] {
  const collectionNode = getSortedCollectionNodes(input.structure).find(
    (node) => node.rootFolderSlug === input.rootFolderSlug && node.slug === input.collectionSlug
  );
  if (!collectionNode) {
    return [];
  }

  return buildResolvedCreateActions({
    node: collectionNode,
    parentEntities: input.parentEntities,
  });
}

/**
 * Finds the create actions for one entity type anywhere under a root folder, without needing to
 * know which collection node hosts it. Top-level types (dataset, dashboard, …) sit on collection
 * nodes directly under the root; nested types (field, dashboardPage, dashboardVisual,
 * dashboardFilter, agentTool, agentKnowledge) sit on `fromParent` collection nodes further down,
 * and their action carries the `parentBinding` naming the parent type a picker must resolve.
 *
 * Callers that already know the parent entity can pass it as `parentEntities`; callers that pick
 * one interactively can instead hand it to `useCreateEntityFromAction` as `parentEntity`.
 */
export function resolveEntityTypeCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  entityTypeKey: string;
  parentEntities?: CreateActionParentEntity[];
}): ResolvedCreateAction[] {
  return getSortedCollectionNodes(input.structure)
    .filter((node) => node.rootFolderSlug === input.rootFolderSlug)
    .flatMap((node) =>
      buildResolvedCreateActions({
        node,
        parentEntities: input.parentEntities,
      })
    )
    .filter((resolved) => resolved.action.targetEntityTypeKey === input.entityTypeKey);
}

/**
 * The single create action for an entity type under a root folder, or null when the framework
 * does not offer one — map rows degrade gracefully rather than rendering a dead control.
 */
export function resolveEntityTypeCreateAction(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  entityTypeKey: string;
  parentEntities?: CreateActionParentEntity[];
}): ResolvedCreateAction | null {
  return resolveEntityTypeCreateActions(input)[0] ?? null;
}

export function resolveFolderCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  folderSlug: string;
  parentEntities?: CreateActionParentEntity[];
}): ResolvedCreateAction[] {
  const folderNode = (input.structure?.folderNodes ?? [])
    .filter((node): node is CompiledFolderNode => node.kind !== "root")
    .find(
      (node) =>
        node.rootFolderSlug === input.rootFolderSlug &&
        node.slug === input.folderSlug &&
        node.kind === "folder"
    );
  if (!folderNode) {
    return [];
  }

  return buildResolvedCreateActions({
    node: folderNode,
    parentEntities: input.parentEntities,
  });
}

export function resolveHeaderCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  parentEntity: Omit<CreateActionParentEntity, "alias">;
}): ResolvedCreateAction[] {
  const actions: ResolvedCreateAction[] = [];

  for (const node of getSortedCollectionNodes(input.structure)) {
    if (node.rootFolderSlug !== input.rootFolderSlug) {
      continue;
    }

    if (!node.createActions?.length) {
      continue;
    }

    for (const action of node.createActions) {
      const binding = action.parentBinding;
      if (!binding || binding.entityTypeKey !== input.parentEntity.entityTypeKey) {
        continue;
      }

      actions.push({
        sourceNodeKey: node.key,
        action,
        parentEntities: [
          {
            alias: binding.alias,
            entityTypeKey: input.parentEntity.entityTypeKey,
            entityId: input.parentEntity.entityId,
            entitySlug: input.parentEntity.entitySlug,
          },
        ],
      });
    }
  }

  return actions;
}

export function resolveSiblingCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  entityTypeKey: string;
  entity: Record<string, unknown>;
  parentEntities?: CreateActionParentEntity[];
}): ResolvedCreateAction[] {
  const parentValues = buildCollectionParentValues(input.parentEntities);

  const collectionNode = getSortedCollectionNodes(input.structure).find(
    (node) =>
      node.rootFolderSlug === input.rootFolderSlug &&
      node.entityTypeKey === input.entityTypeKey &&
      evaluateCollectionMembership({
        entity: input.entity,
        filter: node.filter,
        filterFnKey: node.filterFnKey,
        parentValues,
        pathScope: {
          rootFolderKey: node.rootFolderKey,
          rootFolderSlug: node.rootFolderSlug,
          folderNodeKeys: [node.key],
          folderSlugs: [node.slug],
          entityLineage: [],
        },
      })
  );

  if (!collectionNode?.createActions?.length) {
    return [];
  }

  return collectionNode.createActions.map((action) => ({
    sourceNodeKey: collectionNode.key,
    action,
    parentEntities: input.parentEntities,
  }));
}

export function resolveRootCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderKey: string;
}): ResolvedCreateAction[] {
  const rootNode = input.structure?.folderNodes?.find(
    (node) => node.kind === "root" && node.rootFolderKey === input.rootFolderKey
  );
  if (!rootNode) {
    return [];
  }

  return buildResolvedCreateActions({
    node: rootNode,
  });
}

export function resolveRootFolderChildCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  rootPath?: string;
}): ResolvedCreateAction[] {
  const rootNode = input.structure?.folderNodes?.find(
    (node) => node.kind === "root" && node.rootFolderSlug === input.rootFolderSlug
  );
  if (!rootNode) {
    return [];
  }

  return (input.structure?.folderNodes ?? [])
    .filter(
      (node): node is CompiledFolderNode =>
        node.rootFolderSlug === input.rootFolderSlug && node.parentKey === rootNode.key
    )
    .sort((left, right) => left.listOrder - right.listOrder)
    .flatMap((node) =>
      withSourcePath(
        buildResolvedCreateActions({
          node,
        }),
        joinPathSegments(input.rootPath ?? `/${input.rootFolderSlug}`, node.slug)
      )
    );
}

export function resolveTopLevelFolderCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
}): ResolvedCreateAction[] {
  const rootNode = input.structure?.folderNodes?.find(
    (node) => node.kind === "root" && node.rootFolderSlug === input.rootFolderSlug
  );
  if (!rootNode) {
    return [];
  }

  return getSortedCollectionNodes(input.structure)
    .filter(
      (node) => node.rootFolderSlug === input.rootFolderSlug && node.parentKey === rootNode.key
    )
    .flatMap((node) =>
      buildResolvedCreateActions({
        node,
      })
    );
}

export function resolveChildCollectionCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  rootFolderSlug: string;
  parentNodeKey: string;
  parentEntities?: CreateActionParentEntity[];
}): ResolvedCreateAction[] {
  return getSortedCollectionNodes(input.structure)
    .filter(
      (node) =>
        node.rootFolderSlug === input.rootFolderSlug && node.parentKey === input.parentNodeKey
    )
    .flatMap((node) =>
      buildResolvedCreateActions({
        node,
        parentEntities: input.parentEntities,
      })
    );
}

function dedupeTreeNodeCreateActions(actions: ResolvedCreateAction[]) {
  const preferredActions = new Map<string, ResolvedCreateAction>();

  for (const action of actions) {
    const actionKey = `${action.action.targetEntityTypeKey}:${action.action.presetKey}`;
    const existing = preferredActions.get(actionKey);
    const nextHasParents = (action.parentEntities?.length ?? 0) > 0;
    const existingHasParents = (existing?.parentEntities?.length ?? 0) > 0;

    if (!existing || (nextHasParents && !existingHasParents)) {
      preferredActions.set(actionKey, action);
    }
  }

  return Array.from(preferredActions.values());
}

export function resolveTreeNodeCreateActions(input: {
  structure: Pick<
    CompiledFrameworkStructure,
    "folderNodes" | "entityDisplays" | "topologyRules"
  > | null;
  item: TreeItem;
}): ResolvedCreateAction[] {
  const metadata = input.item.metadata;
  if (!metadata?.rootFolderSlug || !metadata.rootFolderKey) {
    return [];
  }

  if (input.item.type === "root-folder") {
    return dedupeTreeNodeCreateActions(
      resolveRootFolderChildCreateActions({
        structure: input.structure,
        rootFolderSlug: metadata.rootFolderSlug,
        rootPath: input.item.path,
      })
    );
  }

  if (input.item.type === "project-tree-folder") {
    if (metadata.frameworkNodeKey && !metadata.projectTreeFolderId) {
      const nodeActions = resolveNodeCreateActions({
        structure: input.structure,
        rootFolderSlug: metadata.rootFolderSlug,
        nodeKey: metadata.frameworkNodeKey,
        parentEntities: metadata.parentEntities,
      });
      if (nodeActions.length > 0) {
        return withSourcePath(nodeActions, input.item.path);
      }

      return dedupeTreeNodeCreateActions(
        withSourcePath(
          resolveChildCollectionCreateActions({
            structure: input.structure,
            rootFolderSlug: metadata.rootFolderSlug,
            parentNodeKey: metadata.frameworkNodeKey,
            parentEntities: metadata.parentEntities,
          }),
          input.item.path
        )
      );
    }

    if (!metadata.projectTreeFolderId) {
      return [];
    }

    return dedupeTreeNodeCreateActions(
      withSourcePath(
        resolveTopLevelFolderCreateActions({
          structure: input.structure,
          rootFolderSlug: metadata.rootFolderSlug,
        }),
        input.item.path
      )
    );
  }

  if (metadata.collectionSlug) {
    const siblingActions = metadata.frameworkNodeKey
      ? resolveNodeCreateActions({
          structure: input.structure,
          rootFolderSlug: metadata.rootFolderSlug,
          nodeKey: metadata.frameworkNodeKey,
          parentEntities: metadata.parentEntities,
        })
      : resolveCollectionCreateActions({
          structure: input.structure,
          rootFolderSlug: metadata.rootFolderSlug,
          collectionSlug: metadata.collectionSlug,
          parentEntities: metadata.parentEntities,
        });

    if (!metadata.entityTypeKey || !metadata.entityId || !metadata.entitySlug) {
      return withSourcePath(siblingActions, input.item.path);
    }

    const childActions = resolveHeaderCreateActions({
      structure: input.structure,
      rootFolderSlug: metadata.rootFolderSlug,
      parentEntity: {
        entityTypeKey: metadata.entityTypeKey,
        entityId: metadata.entityId,
        entitySlug: metadata.entitySlug,
      },
    });

    return dedupeTreeNodeCreateActions(
      withSourcePath([...childActions, ...siblingActions], input.item.path)
    );
  }

  if (!metadata.entityTypeKey || !metadata.entityId || !metadata.entitySlug) {
    return [];
  }

  const childActions = resolveHeaderCreateActions({
    structure: input.structure,
    rootFolderSlug: metadata.rootFolderSlug,
    parentEntity: {
      entityTypeKey: metadata.entityTypeKey,
      entityId: metadata.entityId,
      entitySlug: metadata.entitySlug,
    },
  });

  const sameTypeRootSiblingActions = resolveRootCreateActions({
    structure: input.structure,
    rootFolderKey: metadata.rootFolderKey,
  }).filter((action) => action.action.targetEntityTypeKey === metadata.entityTypeKey);

  return dedupeTreeNodeCreateActions(
    withSourcePath([...childActions, ...sameTypeRootSiblingActions], input.item.path)
  );
}
